// A route the user's role does not allow shows why instead of a page of refused requests. The
// API enforces every permission; this only spares the user the errors.
import { LockSimple } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import { useT } from "../i18n";
import { PageHeader } from "../ui/PageHeader";
import { EmptyState, Loading } from "../ui/states";
import { type Permission, usePermissions } from "./permissions";

export type AnyOf = readonly [Permission, ...Permission[]];

/**
 * `permission: "super-admin"` is for the platform pages, which belong to the operator; a list
 * allows a role holding any one of them.
 */
export function RequirePermission({ permission, children }: { permission: Permission | AnyOf | "super-admin"; children: ReactNode }) {
  const t = useT().auth.guard;
  const { ready, superAdmin, can } = usePermissions();
  if (!ready) return <Loading label={t.checkingAccess} />;
  const allowed = permission === "super-admin" ? superAdmin : typeof permission === "string" ? can(permission) : permission.some(can);
  if (!allowed) {
    return (
      <>
        <PageHeader title={t.notAvailable} breadcrumb={[{ name: t.home, href: "/" }, { name: t.notAvailable }]} />
        <EmptyState icon={<LockSimple weight="duotone" />} title={t.notAvailableToRole}>
          {permission === "super-admin" ? t.superAdminOnly : t.needsPermission(typeof permission === "string" ? permission : permission[0])} {t.askAdmin}
        </EmptyState>
      </>
    );
  }
  return <>{children}</>;
}
